import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import type { PharmacySearchResult } from '@/types/interfaces/pharmacy'

/**
 * A pharmacy on the patient's list. Resolved against the pharmacy directory when possible; a pharmacy that exists only
 * in the fulfillment owner's records carries just the fields it knows.
 */
export type PreferredPharmacy = Pick<PharmacySearchResult, 'name' | 'address' | 'phone' | 'distance_miles'> &
    Partial<Omit<PharmacySearchResult, 'name' | 'address' | 'phone' | 'distance_miles' | 'ncpdp_provider_id'>> & {
        // Null for a pharmacy that is not in the directory.
        ncpdp_provider_id: string | null
    }

export const preferredPharmacyApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // The patient's preferred pharmacy followed by their recent ones; empty when none is on file.
        getPreferredPharmacy: builder.query<{ pharmacies: PreferredPharmacy[] }, void>({
            query: () => apiEndpoints.preferredPharmacy,
            providesTags: ['PreferredPharmacy'],
        }),
        updatePreferredPharmacy: builder.mutation<{ pharmacy: PreferredPharmacy }, { ncpdpProviderId: string }>({
            query: ({ ncpdpProviderId }) => ({
                url: apiEndpoints.preferredPharmacy,
                method: 'PUT',
                body: { ncpdp_provider_id: ncpdpProviderId },
            }),
            // Search flags the saved pharmacy as the default, so cached results are stale after a change.
            invalidatesTags: ['PharmacySearch'],
            // The owner applies a change asynchronously, so a re-read straight after saving could return the old
            // list. The saved pharmacy is put first in the cached list instead, and the response seeds the cache
            // when no list is cached yet.
            onQueryStarted: async (_arguments, { dispatch, queryFulfilled }) => {
                // A rejected save leaves the cached list as it was; the caller handles the error.
                const savedResponse = await queryFulfilled.catch(() => undefined)
                if (!savedResponse) return

                const { pharmacy } = savedResponse.data
                let hasCachedList = false
                dispatch(
                    preferredPharmacyApi.util.updateQueryData('getPreferredPharmacy', undefined, (cachedList) => {
                        hasCachedList = true
                        cachedList.pharmacies = [
                            pharmacy,
                            ...cachedList.pharmacies.filter(
                                ({ ncpdp_provider_id }) => ncpdp_provider_id !== pharmacy.ncpdp_provider_id,
                            ),
                        ]
                    }),
                )
                if (!hasCachedList) {
                    dispatch(preferredPharmacyApi.util.upsertQueryData('getPreferredPharmacy', undefined, { pharmacies: [pharmacy] }))
                }
            },
        }),
    }),
    overrideExisting: false,
})

export const { useGetPreferredPharmacyQuery, useUpdatePreferredPharmacyMutation } = preferredPharmacyApi
